import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Check, Printer, CheckCircle2 } from 'lucide-react';

const api = axios.create({ baseURL: '/api/propuestas' });
const money = (v) => '$' + Number(v || 0).toLocaleString('es-AR');
const fecha = (d) => (d ? String(d).slice(0, 10).split('-').reverse().join('/') : '');
const DEGRADE = 'linear-gradient(135deg, #0B1C33 0%, #14487F 55%, #2E7DE2 100%)';

const TIPO = { abono: 'por mes', anual: 'por año', unico: '', proyecto: '' };

/* La propuesta que abre el cliente desde el enlace del mail. Sin login. */
export default function Propuesta() {
  const token = window.location.pathname.split('/propuesta/')[1] || '';
  const [p, setP] = useState(null);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(false);
  const [firma, setFirma] = useState({ nombre: '', comentario: '' });
  const [aceptada, setAceptada] = useState(false);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    api.get('/' + token)
      .then(r => { setP(r.data); setAceptada(r.data.estado === 'confirmada'); })
      .catch(e => setError(e.response?.data?.error || 'No pudimos abrir la propuesta'));
  }, [token]);

  const aceptar = async (e) => {
    e.preventDefault();
    setEnviando(true);
    try {
      await api.post(`/${token}/aceptar`, firma);
      setAceptada(true); setModal(false);
      toast.success('¡Gracias! Ya nos llegó tu confirmación');
    } catch (err) { toast.error(err.response?.data?.error || 'No se pudo confirmar'); }
    finally { setEnviando(false); }
  };

  if (error) return <Centro>{error}</Centro>;
  if (!p) return <Centro>Cargando...</Centro>;

  const d = p.datos || {};
  const items = d.items || [];
  const total = p.monto || items.reduce((a, i) => a + Number(i.monto || 0), 0);
  const seccion = { padding: '26px 0', borderTop: '1px solid var(--border)' };
  const h2 = { fontSize: 13, fontWeight: 800, letterSpacing: 1.6, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 14 };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-primary)' }}>
      <style>{`@media print {
        .no-imprimir { display: none !important; }
        body { background: #fff !important; }
      }`}</style>

      {/* Portada */}
      <div style={{ background: DEGRADE, color: '#fff', padding: '46px 20px 40px' }}>
        <div style={{ maxWidth: 780, margin: '0 auto' }}>
          <img src="/icon-192.png?v=2" alt="U.dream" style={{ width: 54, height: 54, borderRadius: 14 }} />
          <div style={{ fontSize: 11, letterSpacing: 2.5, textTransform: 'uppercase', opacity: 0.7, marginTop: 16 }}>Propuesta de trabajo</div>
          <h1 style={{ fontSize: 32, fontWeight: 800, margin: '6px 0 10px', lineHeight: 1.15 }}>{p.titulo}</h1>
          <div style={{ fontSize: 15, opacity: 0.85 }}>
            {p.cliente ? `Para ${p.cliente}` : ''}{p.enviada_at ? ` · ${fecha(p.enviada_at)}` : ''}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 780, margin: '0 auto', padding: '0 20px 60px' }}>
        {p.vencida && !aceptada && (
          <div className="no-imprimir" style={{ background: 'rgba(245,158,11,0.12)', color: '#f59e0b', padding: '10px 14px', borderRadius: 10, fontSize: 13, marginTop: 20 }}>
            Esta propuesta venció el {fecha(p.vence)}. Escribinos y la actualizamos con gusto.
          </div>
        )}

        {d.intro && (
          <div style={{ ...seccion, borderTop: 'none', fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', whiteSpace: 'pre-line' }}>{d.intro}</div>
        )}

        {(d.objetivos || []).length > 0 && (
          <div style={seccion}>
            <div style={h2}>Objetivos</div>
            {d.objetivos.map((o, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 10, fontSize: 15, lineHeight: 1.5 }}>
                <Check size={18} style={{ color: '#2E7DE2', flexShrink: 0, marginTop: 2 }} />{o}
              </div>
            ))}
          </div>
        )}

        {items.length > 0 && (
          <div style={seccion}>
            <div style={h2}>Qué incluye</div>
            {items.map((it, i) => (
              <div key={i} style={{ display: 'flex', gap: 16, padding: '14px 0', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 260px' }}>
                  <div style={{ fontWeight: 700, fontSize: 16 }}>{it.titulo}</div>
                  {it.detalle && <div style={{ fontSize: 14, color: 'var(--text-secondary)', marginTop: 4, lineHeight: 1.6 }}>{it.detalle}</div>}
                </div>
                {Number(it.monto) > 0 && (
                  <div style={{ fontWeight: 700, fontSize: 16, whiteSpace: 'nowrap' }}>
                    {money(it.monto)}<span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}> {TIPO[it.tipo] || ''}</span>
                  </div>
                )}
              </div>
            ))}
            {total > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 16, fontSize: 18, fontWeight: 800 }}>
                <span>Total</span><span>{money(total)}</span>
              </div>
            )}
          </div>
        )}

        {((d.incluye || []).length > 0 || (d.no_incluye || []).length > 0) && (
          <div style={{ ...seccion, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 24 }}>
            {(d.incluye || []).length > 0 && (
              <div><div style={h2}>También incluye</div>
                {d.incluye.map((x, i) => <div key={i} style={{ fontSize: 14, marginBottom: 8, color: 'var(--text-secondary)' }}>· {x}</div>)}</div>
            )}
            {(d.no_incluye || []).length > 0 && (
              <div><div style={h2}>No incluye</div>
                {d.no_incluye.map((x, i) => <div key={i} style={{ fontSize: 14, marginBottom: 8, color: 'var(--text-muted)' }}>· {x}</div>)}</div>
            )}
          </div>
        )}

        {(d.plan_pago || []).length > 0 && (
          <div style={seccion}>
            <div style={h2}>Plan de pago</div>
            {d.plan_pago.map((c, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--border)', fontSize: 15 }}>
                <span>{c.concepto}{c.vencimiento ? <span style={{ color: 'var(--text-muted)', fontSize: 13 }}> · {fecha(c.vencimiento)}</span> : ''}</span>
                <b>{money(c.monto)}</b>
              </div>
            ))}
          </div>
        )}

        {(d.plazo || d.condiciones) && (
          <div style={seccion}>
            <div style={h2}>Plazos y condiciones</div>
            {d.plazo && <div style={{ fontSize: 15, marginBottom: 10 }}><b>Entrega:</b> {d.plazo}</div>}
            {d.condiciones && <div style={{ fontSize: 14, color: 'var(--text-secondary)', whiteSpace: 'pre-line', lineHeight: 1.7 }}>{d.condiciones}</div>}
            {p.vence && <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 12 }}>Esta propuesta tiene validez hasta el {fecha(p.vence)}.</div>}
          </div>
        )}

        {/* Cierre */}
        <div style={{ marginTop: 32, padding: 26, borderRadius: 16, background: DEGRADE, color: '#fff', textAlign: 'center' }}>
          {aceptada ? (<>
            <CheckCircle2 size={34} style={{ marginBottom: 10 }} />
            <div style={{ fontSize: 19, fontWeight: 800 }}>¡Propuesta aceptada!</div>
            <div style={{ fontSize: 14, opacity: 0.85, marginTop: 6 }}>Nos ponemos en marcha y te escribimos para coordinar los próximos pasos.</div>
          </>) : (<>
            <div style={{ fontSize: 19, fontWeight: 800 }}>¿Arrancamos?</div>
            <div style={{ fontSize: 14, opacity: 0.85, marginTop: 6, marginBottom: 18 }}>Con un clic nos confirmás y empezamos a trabajar.</div>
            <button className="no-imprimir btn" style={{ background: '#fff', color: '#0B1C33', fontWeight: 700, padding: '12px 26px' }} onClick={() => setModal(true)}>
              Aceptar la propuesta
            </button>
          </>)}
        </div>

        <div className="no-imprimir" style={{ textAlign: 'center', marginTop: 22 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => window.print()}><Printer size={15} /> Descargar en PDF</button>
        </div>
        <div style={{ textAlign: 'center', marginTop: 20, fontSize: 11, color: 'var(--text-muted)' }}>Udream App · udream.com.ar</div>
      </div>

      {modal && (
        <div className="modal-overlay no-imprimir" onClick={() => setModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <h2>Confirmar la propuesta</h2>
            <form onSubmit={aceptar}>
              <div className="form-group"><label>Tu nombre</label>
                <input value={firma.nombre} onChange={e => setFirma({ ...firma, nombre: e.target.value })} required autoFocus placeholder="Quién confirma" /></div>
              <div className="form-group"><label>Querés agregar algo (opcional)</label>
                <textarea rows={3} value={firma.comentario} onChange={e => setFirma({ ...firma, comentario: e.target.value })} style={{ fontFamily: 'inherit', fontSize: 13 }} /></div>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={enviando}>{enviando ? 'Confirmando...' : 'Confirmar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const Centro = ({ children }) => (
  <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', padding: 20, textAlign: 'center' }}>{children}</div>
);
